import { useCallback, useRef, useState, type DragEventHandler } from 'react';

interface UseComposerFileDropOptions {
  /** When false, drag events are ignored (read-only composer, disabled state). */
  enabled: boolean;
  onFilesDropped: (files: FileList | readonly File[] | null) => void;
}

export interface ComposerFileDropBindings {
  isDragOver: boolean;
  onDragEnter: DragEventHandler<HTMLElement>;
  onDragLeave: DragEventHandler<HTMLElement>;
  onDragOver: DragEventHandler<HTMLElement>;
  onDrop: DragEventHandler<HTMLElement>;
}

/**
 * Shared drag-and-drop staging for Work composers. Uses a counter so
 * nested children don't flicker the overlay when the pointer crosses
 * chip rows or the rich-text editor.
 */
export const useComposerFileDrop = ({
  enabled,
  onFilesDropped,
}: UseComposerFileDropOptions): ComposerFileDropBindings => {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const onDragEnter = useCallback<DragEventHandler<HTMLElement>>(
    (event) => {
      if (!enabled) return;
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current += 1;
      if (event.dataTransfer.types.includes('Files')) {
        setIsDragOver(true);
      }
    },
    [enabled]
  );

  const onDragLeave = useCallback<DragEventHandler<HTMLElement>>((event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const onDragOver = useCallback<DragEventHandler<HTMLElement>>((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onDrop = useCallback<DragEventHandler<HTMLElement>>(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      if (!enabled) return;
      onFilesDropped(event.dataTransfer.files);
    },
    [enabled, onFilesDropped]
  );

  return {
    isDragOver,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
  };
};
